import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { APP_PATHS } from '@compflash/contracts';
import { useSession } from './lib/session';
import { AuthPage, ForgotPasswordPage, ResetPasswordPage } from './pages/PublicPages';
import { WorkspacePage } from './pages/WorkspacePage';
import { HistoryPage, LookupPage, WatchlistPage } from './pages/LibraryPages';
import { BillingPage, SettingsPage, StatusPage } from './pages/AccountPages';

export function App(): JSX.Element {
  const { user, loading, signOut, usage } = useSession();

  return (
    <div className="shell">
      <header className="topbar">
        <NavLink to="/" className="brand">
          Comp<span>Flash</span>
        </NavLink>
        <nav>
          {user && (
            <>
              <NavLink to="/scan">Scan</NavLink>
              <NavLink to="/lookup">Lookup</NavLink>
              <NavLink to="/history">History</NavLink>
              <NavLink to="/watchlist">Watchlist</NavLink>
              <NavLink to="/billing">Billing</NavLink>
              <NavLink to="/settings">Settings</NavLink>
              <NavLink to="/status">Status</NavLink>
            </>
          )}
        </nav>
        <div className="topbar-right">
          {usage && usage.scansPerDay !== null && (
            <span className="muted small">
              {usage.scansRemainingToday ?? 0} scans left today
            </span>
          )}
          {user ? (
            <button type="button" className="ghost" onClick={() => void signOut()}>
              Sign out
            </button>
          ) : (
            <>
              <NavLink to="/signin">Sign in</NavLink>
              <NavLink to="/signup" className="primary button">
                Start free
              </NavLink>
            </>
          )}
        </div>
      </header>

      <main>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <Routes>
            {/* Marketing lives on compflash.com as a separate static site, so
                the app never renders a landing page. An anonymous visitor here
                came for the product, not the pitch. */}
            <Route path="/" element={<Navigate to={user ? '/scan' : '/signin'} replace />} />
            <Route path="/signin" element={user ? <Navigate to="/scan" replace /> : <AuthPage mode="signin" />} />
            <Route path="/signup" element={user ? <Navigate to="/scan" replace /> : <AuthPage mode="signup" />} />
            <Route
              path={APP_PATHS.forgotPassword}
              element={user ? <Navigate to="/settings" replace /> : <ForgotPasswordPage />}
            />
            {/* Reachable while signed in on purpose: arriving here with a token
                usually means the account was compromised, and bouncing the
                victim to /scan would strand them. */}
            <Route path={APP_PATHS.resetPassword} element={<ResetPasswordPage />} />
            <Route path="/scan" element={<Protected><WorkspacePage /></Protected>} />
            <Route path="/lookup" element={<Protected><LookupPage /></Protected>} />
            <Route path="/history" element={<Protected><HistoryPage /></Protected>} />
            <Route path="/watchlist" element={<Protected><WatchlistPage /></Protected>} />
            <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/status" element={<Protected><StatusPage /></Protected>} />
            <Route path="*" element={<p className="muted">Page not found.</p>} />
          </Routes>
        )}
      </main>

      <footer className="footer">
        <span>
          Estimates are derived from historical sales and are not a valuation, appraisal or financial advice. No outcome
          or resale price is guaranteed.
        </span>
      </footer>
    </div>
  );
}

function Protected({ children }: { children: ReactNode }): JSX.Element {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) return <p className="muted">Loading…</p>;
  if (!user) return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}
